"use client";
import TimeSelect from "@/components/pages/car/searchForm/TimeSelect";
import { Button } from "@/components/ui/Button";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { dateFormats, isSameDay } from "@/utils/dateFormatter";
import { generateTimeSlots } from "@/utils/generateTimeSlots";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import "react-day-picker/style.css";
import { createPortal } from "react-dom";
import DatePickerContent from "./DatePickerContent";
import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";

const TABS = {
  pickUpTime: "pickUpTime",
  pickUpDate: "pickUpDate",
  dropOffTime: "dropOffTime",
  dropOffDate: "dropOffDate",
};

const DateRangePicker = forwardRef(
  (
    {
      id,
      label,
      value,
      onSelect,
      placeholder,
      disabled,
      onPortalStateChange,
      mobileCheckinClick,
      mobileCheckoutClick,
      mobilePickUpClick,
      mobileDropOffClick,
      pickUpDate,
      dropOffDate,
      defaultMonth,
      publicHolidays,
      pickUpTime,
      dropOffTime,
      setPickUpTime,
      setDropOffTime,
      webDropdownClassName = "",
      scrollToForm,
      onOpen,
      viewportHeightWrapperActive,
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const t = useTranslationDetector("carSearch");
    const [isTimeSelectOpen, setIsTimeSelectOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("");

    // Optimize edilmiş state yönetimi
    const ANIMATION_STATES = useMemo(
      () => ({
        CLOSED: "closed",
        OPENING: "opening",
        OPEN: "open",
        CLOSING: "closing",
      }),
      [],
    );

    const [uiState, setUiState] = useState({
      animationState: ANIMATION_STATES.CLOSED,
      isFocused: false,
      activeTab: "calendar",
    });

    // Derived values
    const isOpen = uiState.animationState !== ANIMATION_STATES.CLOSED;
    const isFocused = uiState.isFocused;
    const shouldRenderPortal =
      isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isAnimating =
      isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const shouldRenderWebDropdown =
      !isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isWebDropdownAnimating =
      !isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const escapePressed = useKeyPressed("Escape");

    const containerRef = useRef(null);
    const pickUpInputRef = useRef(null);
    const dropOffInputRef = useRef(null);
    const blurTimeoutRef = useRef(null);

    useEffect(() => {
      if (isOpen) {
        onOpen();
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen && !dropOffDate) {
        onSelect({
          from: pickUpDate,
          to: dropOffDate,
        });
      }
    }, [isOpen, dropOffDate, onSelect, pickUpDate]);

    const handleClickOutside = useCallback(() => {
      // Mobilde portal açıksa click outside'ı devre dışı bırak
      if (isMobile && shouldRenderPortal) {
        return;
      }

      // Web'de dropdown açıksa kapat
      if (!isMobile && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [isMobile, isOpen, shouldRenderPortal, ANIMATION_STATES]);

    useEffect(() => {
      if (escapePressed && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [escapePressed, isOpen, ANIMATION_STATES]);

    useEffect(() => {
      if (isTimeSelectOpen && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [isTimeSelectOpen, isOpen, ANIMATION_STATES]);

    // Her zaman ref ver, kontrol handleClickOutside içinde
    useOnClickOutside(containerRef, handleClickOutside);

    // Portal state değişikliklerini parent'a bildir
    useEffect(() => {
      if (onPortalStateChange && isMobile) {
        onPortalStateChange(shouldRenderPortal);
      }
    }, [shouldRenderPortal, isMobile, onPortalStateChange]);

    // Body scroll yönetimini parent'a bırak - artık burada yapma
    // useLockBodyScroll(shouldRenderPortal && isMobile);

    // Tek useEffect ile tüm animasyon kontrolü
    useEffect(() => {
      if (uiState.animationState === ANIMATION_STATES.OPENING) {
        const timer = setTimeout(() => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.OPEN,
          }));
        }, 10);
        return () => clearTimeout(timer);
      } else if (uiState.animationState === ANIMATION_STATES.CLOSING) {
        const timer = setTimeout(
          () => {
            setUiState((prev) => ({
              ...prev,
              animationState: ANIMATION_STATES.CLOSED,
            }));
          },
          isMobile ? 300 : 200,
        );
        return () => clearTimeout(timer);
      }
    }, [uiState.animationState, isMobile, ANIMATION_STATES]);

    const handleDateSelect = useCallback(
      (date, triggerDate) => {
        // Clear any pending blur timeout
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }

        // if (!dropOffDate && !isMobile) {
        //   setUiState((prev) => ({
        //     ...prev,
        //     animationState: ANIMATION_STATES.CLOSING,
        //     isFocused: false,
        //   }));
        // }

        onSelect?.(date, triggerDate);
      },
      [onSelect, dropOffDate, ANIMATION_STATES.CLOSING, isMobile],
    );

    // Portal close handler
    const handlePortalClose = useCallback(() => {
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.CLOSING,
        isFocused: false,
      }));
    }, [ANIMATION_STATES]);

    const handlePickUpFocus = useCallback(() => {
      setActiveTab(TABS.pickUpDate);

      if (!isMobile && scrollToForm) {
        const inputRect = pickUpInputRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.OPENING,
        isFocused: true,
      }));
    }, [ANIMATION_STATES, scrollToForm, isMobile]);

    const handleDropOffFocus = useCallback(() => {
      setActiveTab(TABS.dropOffDate);

      if (!isMobile && scrollToForm) {
        const inputRect = dropOffInputRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.OPENING,
        isFocused: true,
      }));
    }, [ANIMATION_STATES, scrollToForm, isMobile]);

    // Imperative handle to allow parent components to open the datepicker
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.OPENING,
            isFocused: true,
          }));
        },
        close: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.CLOSING,
            isFocused: false,
          }));
        },
      }),
      [ANIMATION_STATES.OPENING, ANIMATION_STATES.CLOSING],
    );

    // Blur handler'ı memoize et
    const handleBlur = useCallback(() => {
      setActiveTab("");

      // Mobilde portal açıksa blur handler'ı devre dışı bırak
      if (isMobile && shouldRenderPortal) {
        return;
      }

      // Web'de blur handler'ı devre dışı bırak, sadece click outside ile kontrol et
      if (!isMobile) {
        return;
      }

      // Delay blur to allow onClick events to complete
      blurTimeoutRef.current = setTimeout(() => {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }, 150);
    }, [isMobile, shouldRenderPortal, ANIMATION_STATES.CLOSING]);
    // const displayValue = dateFormats.dayLongMonthShortDay(value.from);

    // CSS class'ları memoize et
    const containerClasses = useMemo(
      () =>
        clsx(
          "border-primary-weak relative flex h-auto w-full flex-col items-center border-b duration-300 ease-in-out md:h-[66px] md:flex-row md:rounded-lg md:border-0 md:max-w-max",
        ),
      [],
    );

    const labelClasses = useMemo(
      () =>
        clsx(
          "text-foreground-muted pointer-events-none absolute left-[18px] transition-all duration-300 ease-in-out md:left-5",
          isFocused || value
            ? "top-[11.5px] text-xs"
            : "top-1/2 -translate-y-1/2 text-base",
        ),
      [isFocused, value],
    );

    const inputClasses = useMemo(
      () =>
        clsx(
          "selection:bg-secondary-weak h-full w-full rounded-lg border-0 font-semibold transition-all",
          "duration-300 ease-in-out focus:ring-0 focus:outline-none w-[93px]",
          "px-0 pt-[26px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]",
        ),
      [isFocused, value],
    );

    // Portal container sınıfları - backdrop yok, direkt tam ekran
    const portalContainerClasses = useMemo(() => {
      return clsx(
        "fixed inset-0 z-50 flex h-full flex-col bg-white",
        "transform transition-all duration-300",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
      );
    }, [isAnimating]);

    // Web dropdown sınıfları
    const webDropdownClasses = useMemo(() => {
      return clsx(
        "shadow-xxl absolute top-full z-50 mt-[5px] w-[860px] min-w-[320px] overflow-hidden rounded-lg bg-white md:right-0 xl:-left-[100px]",
        "origin-top transform transition-all duration-200 ease-out",
        isWebDropdownAnimating
          ? "translate-y-0 scale-100 opacity-100"
          : "-translate-y-2 scale-95 opacity-0",
        webDropdownClassName
      );
    }, [isWebDropdownAnimating]);

    /*
    const ringClasses = useCallback(({ extraW = 0, left = 0 }) => {
      return clsx(
        `relative before:absolute before:top-[-2px] before:left-[${left}px] before:content-['']`,
        `before:h-[calc(100%+4px)] before:w-[calc(100%+${extraW}px)] before:rounded-lg`,
        "before:border-primary before:border before:border-[2px]",
      );
    }, []);
     */

    const ringClasses = useMemo(() => {
      return clsx("md:ring-primary md:ring-2 md:rounded-lg");
    }, []);

    // Render content component
    const renderDatePickerContent = useCallback(() => {
      return (
        <DatePickerContent
          mode="range"
          value={value}
          handleDateSelect={handleDateSelect}
          defaultMonth={defaultMonth}
          pickUpDate={pickUpDate}
          dropOffDate={dropOffDate}
          publicHolidays={publicHolidays}
        />
      );
    }, [
      value,
      handleDateSelect,
      defaultMonth,
      pickUpDate,
      dropOffDate,
      publicHolidays,
    ]);

    const today = useMemo(() => {
      return new Date();
    }, []);

    const pickUpTimeSlots = useMemo(() => {
      let pickUpSlots = [];

      if (isSameDay(pickUpDate, today)) {
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        pickUpSlots = generateTimeSlots(
          [+currentHour, currentMinute].join(":"),
        );
      } else {
        pickUpSlots = generateTimeSlots();
      }

      return pickUpSlots
        .filter((s) => !s.disabled)
        .map((s) => ({ value: s.slot, label: s.slot }));
    }, [pickUpDate, today]);

    const dropOffTimeSlots = useMemo(() => {
      let dropOffSlots = [];

      if (isSameDay(pickUpDate, dropOffDate) && pickUpTime) {
        const [pickUpHour, pickUpMinute] = pickUpTime.split(":");
        dropOffSlots = generateTimeSlots([+pickUpHour, pickUpMinute].join(":"));
      } else {
        dropOffSlots = generateTimeSlots();
      }

      return dropOffSlots
        .filter((s) => !s.disabled)
        .map((s) => ({ value: s.slot, label: s.slot }));
    }, [pickUpDate, dropOffDate, pickUpTime]);

    // Handle pickup time updates
    useEffect(() => {
      if (pickUpTime && isSameDay(pickUpDate, today)) {
        const enabledSlots = pickUpTimeSlots.map(({ value }) => value);
        if (!enabledSlots.includes(pickUpTime)) {
          setPickUpTime(enabledSlots[0]);
        }
      }
    }, [pickUpDate, pickUpTime, pickUpTimeSlots, today]);

    // Handle dropoff time updates
    useEffect(() => {
      if (dropOffTime && isSameDay(pickUpDate, dropOffDate) && pickUpTime) {
        const enabledSlots = dropOffTimeSlots.map(({ value }) => value);
        if (!enabledSlots.includes(dropOffTime)) {
          setDropOffTime(enabledSlots[0]);
        }
      }
    }, [pickUpDate, dropOffDate, pickUpTime, dropOffTime, dropOffTimeSlots]);

    return (
      <>
        <div
          className="relative w-full md:max-w-max"
          ref={containerRef}
          data-datepicker="true"
        >
          <div
            // className="border-primary-weak flex items-center border-b md:border-0"
            className={containerClasses}
          >
            <div className="border-primary-weak flex h-[66px] w-full justify-between border-b max-md:pr-[18px] md:h-full md:border-0 md:w-max">
              <div
                className={clsx(
                  "relative h-full pl-[18px] w-full md:w-[117px] xl:w-[127px]",
                  !isMobile && activeTab === TABS.pickUpDate && ringClasses,
                )}
              >
                {label && (
                  <label htmlFor={id} className={labelClasses}>
                    {t("pickUpDate")}
                  </label>
                )}
                <input
                  ref={pickUpInputRef}
                  type="text"
                  id={id}
                  readOnly
                  value={dateFormats.dayShortMonthShortDayWithComma(
                    value?.from,
                  )}
                  placeholder={placeholder}
                  onFocus={handlePickUpFocus}
                  onBlur={handleBlur}
                  className={inputClasses}
                />
              </div>

              <div className="flex h-full items-end py-3 md:p-0">
                <span className="bg-border block h-full w-[1px] md:mb-4 md:h-4" />
              </div>

              <div
                className={clsx(
                  "relative flex items-end justify-end pb-3 pl-[18px] max-md:pt-3 md:pr-3 md:pb-[12px] md:pl-4",
                  !isMobile && activeTab === TABS.pickUpTime && ringClasses,
                )}
              >
                <label
                  htmlFor={`${id}_pickUpTime`}
                  className={clsx(labelClasses, "md:hidden")}
                >
                  {t("pickUpTime")}
                </label>

                <TimeSelect
                  id={`${id}_pickUpTime`}
                  onSelect={setPickUpTime}
                  value={pickUpTime}
                  timeOptions={pickUpTimeSlots}
                  onClicked={(s) => {
                    setIsTimeSelectOpen(s);
                  }}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  onFocus={() => {
                    setActiveTab(TABS.pickUpTime);
                  }}
                  onBlur={() => {
                    setActiveTab("");
                  }}
                  bottomSheetTitle={t("pickUpTime")}
                />
              </div>
            </div>
            <span className="bg-border hidden h-[34px] w-[1px] md:block" />
            <div className="flex h-[66px] w-full justify-between max-md:pr-[18px] md:h-full md:w-max">
              <div
                className={clsx(
                  "relative h-full pl-[18px] w-full md:w-[117px] xl:w-[127px]",
                  !isMobile && activeTab === TABS.dropOffDate && ringClasses,
                )}
              >
                {label && (
                  <label htmlFor={id} className={labelClasses}>
                    {t("dropOffDate")}
                  </label>
                )}
                <input
                  ref={dropOffInputRef}
                  type="text"
                  id={id}
                  readOnly
                  value={
                    dropOffDate
                      ? dateFormats.dayShortMonthShortDayWithComma(value?.to)
                      : ""
                  }
                  placeholder={placeholder}
                  onFocus={handleDropOffFocus}
                  onBlur={handleBlur}
                  className={inputClasses}
                />
              </div>

              <div className="flex h-full items-end py-3 md:p-0">
                <span className="bg-border block h-full w-[1px] md:mb-4 md:h-4" />
              </div>

              <div
                className={clsx(
                  "relative flex items-end justify-end pb-3 pl-[18px] max-md:pt-3 md:pr-3 md:pb-[12px] md:pl-4",
                  !isMobile && activeTab === TABS.dropOffTime && ringClasses,
                )}
              >
                <label
                  htmlFor={`${id}_dropOffTime`}
                  className={clsx(labelClasses, "md:hidden")}
                >
                  {t("dropOffTime")}
                </label>

                <TimeSelect
                  id={`${id}_dropOffTime`}
                  onSelect={setDropOffTime}
                  value={dropOffTime}
                  timeOptions={dropOffTimeSlots}
                  onClicked={(s) => {
                    setIsTimeSelectOpen(s);
                  }}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  onFocus={() => {
                    setActiveTab(TABS.dropOffTime);
                  }}
                  onBlur={() => {
                    setActiveTab("");
                  }}
                  bottomSheetTitle={t("dropOffTime")}
                />
              </div>
            </div>
          </div>

          {/* Web dropdown */}
          {!isMobile && shouldRenderWebDropdown && (
            <div className={webDropdownClasses}>
              <ViewportHeightWrapper
                defaultMaxHeight={600}
                bottomPadding={32}
                enabled={viewportHeightWrapperActive}
              >
                {renderDatePickerContent()}
              </ViewportHeightWrapper>
            </div>
          )}
        </div>

        {/* Mobile portal - tam ekran slide up modal */}
        {shouldRenderPortal &&
          isMobile &&
          createPortal(
            <div className={portalContainerClasses}>
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between px-4 py-4">
                <span className="w-full text-center text-lg font-semibold">
                  {label}
                </span>
                <button
                  onClick={handlePortalClose}
                  className="self-end disabled:cursor-not-allowed disabled:text-gray-300"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="text-2xl"
                  />
                </button>
              </div>

              {/* Content */}
              <div className="min-h-0 flex-1">
                {renderDatePickerContent()}
                <div className="border-primary-weak relative z-[1] shrink-0 border-t bg-white p-4 md:hidden">
                  <Button
                    onClick={handlePortalClose}
                    className="w-full"
                    size="lg"
                    disabled={!dropOffDate}
                  >
                    {t("applyDateSelect")}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

DateRangePicker.displayName = "DateRangePicker";

export default DateRangePicker;
